import React, { useEffect, useCallback, useRef } from "react";
import { LevelProps, ItemRenderProps, Path } from "../types";
import {
    useCommandDropdown,
    useLevelContext,
    LevelContext,
    matchesSearch,
    pathsEqual,
} from "../context";
import { List, ListItemButton } from "@mui/material";
import { ChevronRight, Check } from "lucide-react";

export function Level<T>({
    options,
    getKey,
    getLabel,
    getCode,
    getSearchKeywords,
    hasChildren: hasChildrenFn,
    allowParentSelect = false,
    renderItem,
    children,
    onSelect,
}: LevelProps<T>) {
    const {
        searchValue,
        filterMode,
        selections,
        onItemSelect,
        registerItems,
        navigationStack,
        pushNavigation,
        activeIndex,
        setActiveIndex,
        registerItemAction,
        clearItemActions,
        registerLabel,
        updateNavigationLabel,
        registerPathForId,
        registerItemForPath,
    } = useCommandDropdown();

    const { depth, parentPath } = useLevelContext();

    // Level
    // - Renders the current hierarchy level based on navigation stack depth.
    // - Registers labels/paths/items to the shared registry so selection/labels work globally.
    // - Handles row clicks (select vs drill) and keyboard action registration.

    // Refs for scrolling active item into view
    const itemRefs = useRef<Map<number, HTMLElement>>(new Map());

    // Determine if this level should be visible
    // Level is visible if its depth matches the navigation stack length
    // depth 0 = root, visible when stack is empty
    // depth 1 = first child level, visible when stack has 1 item
    const isCurrentLevel = depth === navigationStack.length;

    // Get the options for this level
    // If we're at depth > 0, we need to get options from the navigation stack's item
    const levelOptions = React.useMemo(() => {
        if (depth === 0) {
            return options;
        }
        // For nested levels, options are passed via the children render prop
        // The parent level already filters which item's children to show
        return options;
    }, [options, depth]);

    // Register labels for all options in this level (path -> label)
    useEffect(() => {
        if (!levelOptions) return;
        levelOptions.forEach((item) => {
            const key = getKey(item);
            const label = getLabel(item);
            const labelStr = typeof label === "string" ? label : String(key);
            const itemPath = [...parentPath, key];
            registerLabel(itemPath, labelStr);
            registerPathForId(key, itemPath);
            registerItemForPath(itemPath, item);
        });
    }, [
        levelOptions,
        getKey,
        getLabel,
        registerLabel,
        registerPathForId,
        registerItemForPath,
        parentPath,
    ]);

    // Update navigation label for the item we drilled into (breadcrumbs)
    useEffect(() => {
        if (!levelOptions || depth >= navigationStack.length) return;
        const navigatedItem = navigationStack[depth];
        const matchedItem = levelOptions.find((item) => getKey(item) === navigatedItem.key);
        if (!matchedItem) return;
        const label = getLabel(matchedItem);
        const labelStr = typeof label === "string" ? label : String(navigatedItem.key);
        updateNavigationLabel([...parentPath, navigatedItem.key], labelStr);
    }, [levelOptions, navigationStack, depth, getKey, getLabel, updateNavigationLabel, parentPath]);

    // Filter options based on search and the configured filter mode.
    const filteredOptions = React.useMemo(() => {
        if (!levelOptions) return [];
        if (filterMode === "none" || !searchValue.trim()) return levelOptions;

        return levelOptions.filter((item) => {
            const label = getLabel(item);
            const code = getCode?.(item);
            const labelKeyword = typeof label === "string" ? label : undefined;
            const codeKeyword =
                code !== undefined && code !== null && code !== "" ? String(code) : undefined;
            const extraKeywords = getSearchKeywords?.(item) ?? [];
            const keywords = [labelKeyword, codeKeyword, ...extraKeywords].filter(
                (kw): kw is string => !!kw
            );
            return matchesSearch(searchValue, label, keywords);
        });
    }, [levelOptions, searchValue, filterMode, getLabel, getCode, getSearchKeywords]);

    // Register item count for Empty component visibility (only for current visible level).
    useEffect(() => {
        if (isCurrentLevel) {
            registerItems(filteredOptions.length);
        }
    }, [filteredOptions.length, isCurrentLevel, registerItems]);

    // Reset active index when options change or when becoming current level
    useEffect(() => {
        if (isCurrentLevel) {
            setActiveIndex(0);
        }
    }, [filteredOptions, isCurrentLevel, setActiveIndex]);

    // Handle item click/action.
    // If parent selection is not allowed, clicks drill down; otherwise they select.
    const handleItemAction = useCallback(
        (item: T, key: string | number, label: string) => {
            const itemPath: Path = [...parentPath, key];

            // Check if this item has children - ONLY use hasChildrenFn, not the existence of children prop
            const itemHasChildren = hasChildrenFn?.(item) ?? false;
            const canNavigate = itemHasChildren && children;

            if (canNavigate && !allowParentSelect) {
                // When parent selection is not allowed, row clicks drill down.
                pushNavigation(key, label, item);
                return;
            }

            // When parent selection is allowed (or this is a leaf), row clicks select the item.
            onSelect?.(item, itemPath);
            onItemSelect(itemPath, label, item);
        },
        [
            parentPath,
            hasChildrenFn,
            children,
            allowParentSelect,
            pushNavigation,
            onSelect,
            onItemSelect,
        ]
    );

    // Register item actions for keyboard navigation.
    // "select" always maps to row selection; "drill" navigates into children when possible.
    useEffect(() => {
        if (!isCurrentLevel) return;

        // Clear previous actions when this level becomes active
        clearItemActions();

        // Register actions for each item
        filteredOptions.forEach((item, index) => {
            const key = getKey(item);
            const label = getLabel(item);
            const labelStr = typeof label === "string" ? label : String(key);
            const itemHasChildren = hasChildrenFn?.(item) ?? false;
            const canNavigate = itemHasChildren && children;

            registerItemAction(
                index,
                () => {
                    handleItemAction(item, key, labelStr);
                },
                "select"
            );

            if (canNavigate) {
                registerItemAction(
                    index,
                    () => {
                        pushNavigation(key, labelStr, item);
                    },
                    "drill"
                );
            }
        });

        return () => {
            clearItemActions();
        };
    }, [
        isCurrentLevel,
        filteredOptions,
        children,
        hasChildrenFn,
        pushNavigation,
        getKey,
        getLabel,
        handleItemAction,
        registerItemAction,
        clearItemActions,
    ]);

    // Handle mouse enter for active state
    const handleMouseEnter = (index: number) => {
        setActiveIndex(index);
    };

    // Scroll active item into view when activeIndex changes
    useEffect(() => {
        if (!isCurrentLevel) return;

        const activeElement = itemRefs.current.get(activeIndex);
        if (activeElement) {
            activeElement.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
            });
        }
    }, [activeIndex, isCurrentLevel]);

    // Check if a path is selected
    const isPathSelected = useCallback(
        (itemPath: Path): boolean => {
            return selections.some((s) => pathsEqual(s.path, itemPath));
        },
        [selections]
    );

    // Don't render if this level is not the current navigation level
    if (!isCurrentLevel) {
        // But we still need to render children for the navigated item
        if (depth < navigationStack.length && children) {
            const navigatedItem = navigationStack[depth];
            // Find the item that was navigated into
            const parentItem = levelOptions?.find((item) => getKey(item) === navigatedItem.key);
            if (parentItem) {
                return (
                    <LevelContext.Provider
                        value={{ depth: depth + 1, parentPath: [...parentPath, navigatedItem.key] }}
                    >
                        {children(parentItem)}
                    </LevelContext.Provider>
                );
            }
        }
        return null;
    }

    if (!filteredOptions.length) {
        return null;
    }

    return (
        <List dense sx={{ padding: 1, margin: 0 }}>
            {filteredOptions.map((item, index) => {
                const key = getKey(item);
                const label = getLabel(item);
                const labelStr = typeof label === "string" ? label : String(key);
                const code = getCode?.(item);
                // Only show chevron/hasChildren if hasChildrenFn explicitly returns true
                const itemHasChildren = hasChildrenFn?.(item) ?? false;
                const canNavigate = itemHasChildren && children;
                const isActive = index === activeIndex;
                const itemPath: Path = [...parentPath, key];
                const isSelected = isPathSelected(itemPath);

                // Default element for the item
                const checkElement = isSelected ? (
                    <Check size={20} className="text-purple-500" />
                ) : null;
                const chevronElement =
                    canNavigate && (!isSelected || allowParentSelect) ? (
                        allowParentSelect ? (
                            <button
                                type="button"
                                className="inline-flex items-center justify-center w-6 h-6 rounded border border-transparent text-accent hover:bg-[#F9FAFB]"
                                onClick={(event) => {
                                    // Prevent row selection when using the chevron to navigate deeper.
                                    event.stopPropagation();
                                    pushNavigation(key, labelStr, item);
                                }}
                            >
                                <ChevronRight size={18} />
                            </button>
                        ) : (
                            <ChevronRight size={20} className="text-accent" />
                        )
                    ) : null;
                const defaultElement = (
                    <div className="flex flex-row items-center justify-between w-full">
                        <div className="text-accent">{label}</div>
                        <div className="flex items-center gap-2">
                            {code !== undefined && code !== null && code !== "" && (
                                <span className=" text-lightGreen-500 font-thin">{code}</span>
                            )}
                            {checkElement}
                            {chevronElement}
                        </div>
                    </div>
                );

                // Render props for custom rendering
                const itemRenderProps: ItemRenderProps = {
                    isActive,
                    isSelected,
                    isLoading: false,
                    hasChildren: itemHasChildren,
                    depth,
                    defaultElement,
                    checkElement,
                    chevronElement,
                };

                return (
                    <ListItemButton
                        key={key}
                        ref={(el) => {
                            if (el) {
                                itemRefs.current.set(index, el);
                            } else {
                                itemRefs.current.delete(index);
                            }
                        }}
                        selected={isActive}
                        onMouseEnter={() => handleMouseEnter(index)}
                        onClick={() => handleItemAction(item, key, labelStr)}
                        sx={{
                            borderRadius: "8px",
                            mb: 0.5,
                            py: 1,
                            px: 1.5,
                            transition: "none",
                            "&.Mui-selected": {
                                backgroundColor: "action.hover",
                            },
                            "&:hover": {
                                backgroundColor: "action.hover",
                            },
                        }}
                    >
                        {renderItem ? renderItem(item, itemRenderProps) : defaultElement}
                    </ListItemButton>
                );
            })}
        </List>
    );
}

export default Level;
